
import { Idea } from "@/components/IdeaCard";
import { supabase } from "@/integrations/supabase/client";

// Mock data for demo purposes
const mockIdeas: Idea[] = [
  {
    id: "idea1",
    title: "Cardboard Castle",
    description: "Transform cardboard boxes into a medieval castle with drawbridge and towers. Perfect for imaginative play and can be decorated with paint or markers.",
    difficulty: "medium",
    timeRequired: "2 hours",
    materials: [
      "Cardboard boxes",
      "Scissors",
      "Tape or glue",
      "Paint or markers",
      "Construction paper",
      "String (for drawbridge)"
    ],
    instructions: [
      "Cut the top and bottom flaps off a large cardboard box.",
      "Cut out windows and a door on one side of the box.",
      "For the drawbridge, cut a rectangle above the door and attach string.",
      "Create towers by cutting and gluing smaller boxes to the corners.",
      "Paint or decorate the castle with colors and designs.",
      "Add details like flags made from construction paper on toothpicks."
    ],
    storyline: "Once upon a time, there was a magnificent kingdom surrounded by tall mountains and flowing rivers. The royal family needed a new castle to protect them from dragons and other magical creatures. Today, brave builders, you will construct this mighty fortress that will stand for generations and become the center of our magical kingdom!"
  },
  {
    id: "idea2",
    title: "Nature Collage Frame",
    description: "Create a beautiful frame using collected leaves, twigs, and other natural elements. This project encourages children to explore nature and create art simultaneously.",
    difficulty: "easy",
    timeRequired: "1 hour",
    materials: [
      "Cardboard or thick paper",
      "Leaves and flowers",
      "Small twigs",
      "Glue",
      "Scissors",
      "Photo to frame"
    ],
    instructions: [
      "Cut a rectangular frame shape from cardboard.",
      "Collect interesting leaves, flowers, and small twigs from outside.",
      "Arrange the natural elements on the frame before gluing.",
      "Once you're happy with the design, glue the items onto the frame.",
      "Let dry completely before attaching your photo to the back.",
      "Hang your nature frame or prop it up on a shelf to display."
    ],
    storyline: "Deep in the enchanted forest, the fairies collect beautiful leaves, flowers, and twigs to decorate their magical homes. Today, you are honorary forest fairies! Let's venture outside to find nature's treasures and create a frame as beautiful as those adorning the walls of the fairy queen's palace."
  },
  {
    id: "idea3",
    title: "Sock Puppet Theater",
    description: "Transform old socks into character puppets and create a simple theater for storytelling. Encourages creativity, storytelling and dramatic play.",
    difficulty: "easy",
    timeRequired: "1.5 hours",
    materials: [
      "Old socks",
      "Buttons or googly eyes",
      "Yarn or string",
      "Fabric scraps",
      "Glue gun (adult supervision)",
      "Cardboard box for theater"
    ],
    instructions: [
      "Turn a sock inside out and decide where to place the features.",
      "Glue buttons or googly eyes for the puppet's eyes.",
      "Add yarn for hair and fabric scraps for other features.",
      "For the theater, cut the front of a large cardboard box.",
      "Decorate the theater with paint or markers.",
      "Create a simple curtain using fabric and perform your puppet show!"
    ],
    storyline: "Behind the red curtain of the Grand Puppet Theater, magical creatures come to life and tell wonderful stories. Today, you are the master puppeteers who will create these characters from simple socks and bring them to life! What adventures will your puppets have? What stories will they tell? The audience is waiting for your amazing show!"
  }
];

export async function generateIdeas(imageBase64: string): Promise<Idea[]> {
  console.log("Generating ideas from image...");
  
  try {
    // Get the current authenticated user's ID
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    // Call the Supabase Edge Function to process the image with proper error handling
    const { data, error } = await supabase.functions.invoke('process-image', {
      body: { 
        imageBase64,
        userId  // Pass the userId for credit deduction
      }
    });
    
    if (error) {
      console.error("Error calling process-image function:", error);
      throw new Error(error.message);
    }
    
    if (data && data.ideas && Array.isArray(data.ideas) && data.ideas.length > 0) {
      console.log(`Received ${data.ideas.length} ideas from AI`);
      
      // Ensure each idea has all required fields and format them properly
      const formattedIdeas = data.ideas.map((idea: any, index: number) => {
        // Generate a unique ID if missing
        const id = idea.id || `idea_${Date.now()}_${index}`;
        
        // Make sure each idea has all required fields with proper validation
        return {
          id,
          title: idea.title || "Creative Activity",
          description: idea.description || "No description provided",
          difficulty: ["easy", "medium", "hard"].includes(idea.difficulty?.toLowerCase()) 
            ? idea.difficulty.toLowerCase() 
            : "medium",
          timeRequired: idea.timeRequired || "30 minutes",
          materials: Array.isArray(idea.materials) && idea.materials.length > 0 
            ? idea.materials 
            : ["No materials specified"],
          instructions: Array.isArray(idea.instructions) && idea.instructions.length > 0 
            ? idea.instructions 
            : ["No instructions provided"],
          storyline: idea.storyline || "Once upon a time, we discovered a fun activity that would bring joy and creativity to our day. Let's embark on this magical adventure together!"
        };
      });
      
      return formattedIdeas;
    }
    
    // If there's no error but also no valid data, return mock data
    console.log("No valid ideas returned from AI, using mock data");
    return mockIdeas.sort((a, b) => {
      const difficultyOrder = { "easy": 0, "medium": 1, "hard": 2 };
      return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
    });
  } catch (error) {
    console.error("Error generating ideas:", error);
    // For now, fallback to mock data on error
    console.log("Error occurred, using mock data");
    return mockIdeas.sort((a, b) => {
      const difficultyOrder = { "easy": 0, "medium": 1, "hard": 2 };
      return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
    });
  }
}
